import useShortcut from '../../services/shortcuts/useShortcut';

/**
 * Space plays and pauses, from anywhere in the app that is not a text box.
 *
 * The rule is the one a listener would state: if you are not writing
 * something, space is the play button. The shortcut registry
 * (services/shortcuts) keeps it out of inputs, selects and anything
 * editable, and away from a held key and from a key with a modifier on it.
 *
 * Three consequences worth knowing:
 *
 * - **A focused button loses space**, which normally activates it. Enter
 *   still does, so nothing becomes unreachable by keyboard, and the
 *   alternative is that pressing play on a row and then pressing space
 *   restarts that track instead of pausing it.
 * - **The embeds keep their own space.** Key events inside the YouTube and
 *   Mixcloud iframes never reach this document, so once one of those has
 *   focus, space is theirs - and both do the same thing with it anyway.
 *   Once an embed is hidden it is blurred, so it cannot keep the key from
 *   behind the curtain (useBlurWhenHidden).
 * - **The native <audio> controls never hold focus.** Chromium keeps every
 *   key inside them once something in there is focused - a click on the
 *   timeline does that - and space on the timeline toggles nothing, so the
 *   key was simply dead. The player blurs the element the moment it is
 *   focused (see the audio element in player.tsx); the click that focused
 *   it still seeks, and its drag still follows the mouse.
 */
export default function usePlayPauseShortcut(
  /** Toggles playback. Returns false when there was nothing to toggle. */
  toggle: () => boolean,
) {
  useShortcut({
    id: 'play-pause',
    keys: { key: ' ', aliases: ['Spacebar'] },
    group: 'Playback',
    description: 'Play or pause',
    // Nothing to play: the page scrolls, as space always did.
    run: () => toggle(),
  });
}
